import { routeParts, viewPaths, type Cursor, type HealthDto } from "@nabla/api";
import { readAnswer } from "./answers.ts";
import { covers } from "./cursor.ts";
import { getJson, signedOutBy, type ClientError, type Held, type Reach } from "./http.ts";
import type { Hub } from "./hub.ts";
import { interestOf, outdatedBy, touches, type Interest } from "./interest.ts";
import type { DurableEvent } from "./cursor.ts";

export interface ViewEntry {
  readonly key: string;
  readonly held?: Held;
  readonly cursor?: Cursor;
  readonly validUntil: string | null;
  readonly stale: boolean;
  readonly loading: boolean;
  readonly error?: ClientError;
}

const pastValidity = 30_000;

const catchUpWaits = [0, 500, 1_000, 2_000, 4_000, 8_000];

const publicPaths: ReadonlySet<string> = new Set(Object.values(viewPaths).flatMap((route) => ("public" in route && route.public ? [routeParts(route.route).path] : [])));

function headerOf({ body: raw, sameApi }: Held): { readonly cursor: Cursor; readonly validUntil: string | null; readonly health?: HealthDto } | undefined {
  if (!("cursor" in raw)) return undefined;
  const cursor = readAnswer(raw.cursor, sameApi, (api) => api.Cursor);
  const until = "validUntil" in raw ? raw.validUntil : null;
  if (!cursor.ok || !(until === null || typeof until === "string")) return undefined;
  const health = "health" in raw ? readAnswer(raw.health, sameApi, (api) => api.HealthDto) : undefined;
  return { cursor: cursor.value, validUntil: until, ...(health?.ok ? { health: health.value } : {}) };
}

interface ViewsDeps {
  readonly host: Reach;
  readonly hub: Hub;
  readonly after: (ms: number, run: () => void) => () => void;
  readonly now: () => number;
  readonly applied: () => Cursor | null;
  readonly firstSnapshot: (cursor: Cursor) => void;
  readonly health: (health: HealthDto) => void;
  readonly signedOut: () => void;
  readonly generation: () => number;
}

export interface Views {
  entry(key: string): ViewEntry | undefined;
  load(key: string): Promise<void>;
  refetch(key: string): Promise<void>;
  watch(key: string): () => void;
  eventApplied(event: DurableEvent, objects: readonly string[] | "todos"): void;
  allStale(): void;
  reloadAll(): Promise<Cursor[]>;
  invalidate(objects: readonly string[] | "todos", stores: Interest["stores"]): void;
  outOfDate(key: string): void;
  forget(again: boolean): void;
  held(): string[];
}

type After = ViewsDeps["after"];

function createWaits(after: After) {
  const waiting = new Map<string, () => void>();
  return {
    pause: (key: string, ms: number) =>
      new Promise<void>((resolve) => {
        const over = () => {
          waiting.delete(key);
          stop();
          resolve();
        };
        const stop = after(ms, over);
        waiting.set(key, over);
      }),
    end: (key: string) => waiting.get(key)?.(),
    forget(): void {
      for (const over of [...waiting.values()]) over();
    },
  };
}

function createWatching(deps: Pick<ViewsDeps, "after" | "now">, fetch: (key: string) => Promise<void>) {
  const watchers = new Map<string, number>();
  const timers = new Map<string, () => void>();
  const stop = (key: string) => {
    timers.get(key)?.();
    timers.delete(key);
  };
  const arm = (key: string, until: string | null | undefined) => {
    if (!watchers.has(key)) return;
    stop(key);
    if (until === null || until === undefined) return;
    const wait = Date.parse(until) - deps.now();
    timers.set(key, deps.after(wait > 0 ? wait : pastValidity, () => void fetch(key)));
  };
  return {
    arm,
    refetch(key: string): void {
      if (watchers.has(key)) void fetch(key);
    },
    watch(key: string, entry: ViewEntry | undefined): () => void {
      watchers.set(key, (watchers.get(key) ?? 0) + 1);
      if (entry === undefined || entry.stale || (entry.held === undefined && entry.error === undefined)) void fetch(key);
      else arm(key, entry.validUntil);
      return () => {
        const count = (watchers.get(key) ?? 1) - 1;
        if (count > 0) return void watchers.set(key, count);
        watchers.delete(key);
        stop(key);
      };
    },
    forget(): void {
      for (const stop of timers.values()) stop();
      timers.clear();
    },
  };
}

type Watching = ReturnType<typeof createWatching>;

function createEntries(hub: Hub, watching: Watching) {
  const entries = new Map<string, ViewEntry>();
  const marks = new Map<string, number>();
  const set = (key: string, entry: ViewEntry) => {
    entries.set(key, entry);
    hub.touch(`view:${key}`);
    watching.arm(key, entry.validUntil);
  };
  const blank = (key: string): ViewEntry => ({ key, validUntil: null, stale: false, loading: false });
  const patch = (key: string, change: Partial<ViewEntry>) => set(key, { ...(entries.get(key) ?? blank(key)), ...change });
  return {
    get: (key: string) => entries.get(key),
    keys: () => [...entries.keys()],
    values: () => entries.values(),
    set,
    patch,
    marked: (key: string) => marks.get(key),
    outOfDate(key: string): void {
      marks.set(key, (marks.get(key) ?? 0) + 1);
      patch(key, { stale: true });
      watching.refetch(key);
    },
    forget(again: boolean): void {
      marks.clear();
      for (const key of [...entries.keys()]) {
        if (publicPaths.has(key)) continue;
        entries.delete(key);
        hub.touch(`view:${key}`);
        if (again) watching.refetch(key);
      }
    },
  };
}

type Pause = (key: string, ms: number) => Promise<void>;

function createFetching(deps: ViewsDeps, { set, patch, marked }: ReturnType<typeof createEntries>, pause: Pause) {
  const inflight = new Map<string, Promise<void>>();
  function fetchView(key: string): Promise<void> {
    const running = inflight.get(key);
    if (running) return running;
    patch(key, { loading: true });
    const session = deps.generation();
    const left = () => !publicPaths.has(key) && deps.generation() !== session;
    const run = (async () => {
      for (let attempt = 0; ; attempt += 1) {
        const before = marked(key);
        const answer = await getJson(deps.host, key);
        if (left()) return;
        if (!answer.ok) {
          signedOutBy(answer.error, deps.signedOut);
          if (left()) return;
          return patch(key, { loading: false, error: answer.error });
        }
        if (publicPaths.has(key)) return set(key, { key, held: answer.held, validUntil: null, stale: false, loading: false });
        const header = headerOf(answer.held);
        if (!header) return patch(key, { loading: false, error: { kind: "formato", detail: `${key} sem cursor` } });
        const applied = deps.applied();
        if (applied === null) deps.firstSnapshot(header.cursor);
        const behind = applied !== null && !covers(header.cursor, applied);
        const current = !behind && marked(key) === before;
        const wait = current ? undefined : catchUpWaits[attempt];
        const { cursor, validUntil } = header;
        if (!behind) set(key, { key, held: answer.held, cursor, validUntil, stale: !current, loading: wait !== undefined });
        if (!behind && header.health !== undefined) deps.health(header.health);
        if (current) return;
        if (wait === undefined) {
          if (behind) patch(key, { loading: false, stale: true, error: { kind: "formato", detail: `${key} ficou atrás dos eventos já aplicados` } });
          return;
        }
        if (wait > 0) await pause(key, wait);
        if (left()) return;
      }
    })().finally(() => {
      if (inflight.get(key) === run) inflight.delete(key);
    });
    inflight.set(key, run);
    return run;
  }
  return {
    fetch: fetchView,
    forget(): void {
      for (const key of [...inflight.keys()]) if (!publicPaths.has(key)) inflight.delete(key);
    },
  };
}

export function createViews(deps: ViewsDeps): Views {
  const waits = createWaits(deps.after);
  const watching = createWatching(deps, (key) => fetching.fetch(key));
  const entries = createEntries(deps.hub, watching);
  const fetching = createFetching(deps, entries, waits.pause);
  return {
    entry: entries.get,
    load(key) {
      const entry = entries.get(key);
      return entry?.held !== undefined && !entry.stale ? Promise.resolve() : fetching.fetch(key);
    },
    refetch(key) {
      waits.end(key);
      return fetching.fetch(key);
    },
    watch: (key) => watching.watch(key, entries.get(key)),
    eventApplied(event, objects) {
      for (const entry of entries.values()) {
        if (entry.cursor === undefined || !outdatedBy(interestOf(entry.key), entry.cursor, event, objects)) continue;
        entries.patch(entry.key, { stale: true });
        watching.refetch(entry.key);
      }
    },
    allStale() {
      for (const key of entries.keys()) entries.outOfDate(key);
    },
    async reloadAll() {
      const keys = entries.keys();
      for (const key of keys) {
        entries.outOfDate(key);
        waits.end(key);
      }
      await Promise.all(keys.map(fetching.fetch));
      return keys.flatMap((key) => {
        const entry = entries.get(key);
        return entry?.cursor === undefined || entry.stale ? [] : [entry.cursor];
      });
    },
    invalidate(objects, stores) {
      for (const entry of entries.values()) if (touches(interestOf(entry.key), stores, objects)) entries.outOfDate(entry.key);
    },
    outOfDate(key) {
      if (entries.get(key) !== undefined) entries.outOfDate(key);
    },
    forget(again) {
      watching.forget();
      waits.forget();
      fetching.forget();
      entries.forget(again);
    },
    held: entries.keys,
  };
}
